import React from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  Text,
  Divider,
  HStack,
  Badge,
  VStack,
  SimpleGrid,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import BookingStatusTimeline from "./CarBookingStatusTimeline";
import {
  getAllowedStatusesByRole,
  prettyStatus,
  statusColor,
  type AppRole,
  type BookingStatus,
} from "./hireStatusRules";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";

const cardsBorderRadii="8px";

function money(n: number) {
  return `MWK ${Number(n || 0).toLocaleString()}`;
}

const InfoCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const { theme } = useCustomTheme();
  return (
    <Box
      border="1px solid"
      borderColor={theme.border}
      borderRadius={cardsBorderRadii}
      p={3}
      bg={theme.surface}
      boxShadow={theme.shadowSm}
    >
      <Text fontWeight="semibold" mb={2} color={theme.text}>
        {title}
      </Text>
      {children}
    </Box>
  );
};

const KeyVal = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  const { theme } = useCustomTheme();
  return (
    <HStack justify="space-between" align="start" spacing={3}>
      <Text fontSize="sm" color={theme.textSubtle}>
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="medium"
        color={theme.text}
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  );
};

export default function BookingDetailsDrawer({
  isOpen,
  onClose,
  booking,
  mode,
  userRole = "guest",
  onApprove,
  onDecline,
  onCancel,
  onUpdateStatus,
}: {
  isOpen: boolean;
  onClose: () => void;
  booking: any | null;
  mode: "owner" | "customer";
  userRole?: AppRole;
  onApprove?: () => void;
  onDecline?: () => void;
  onCancel?: () => void;
  onUpdateStatus?: (status: BookingStatus) => void;
}) {
  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  if (!booking) return null;

  const b = booking;
  const price = b.price || {};
  const license = b.license || {};

  const canOwnerDecide =
    mode === "owner" && b.status === "awaiting_owner_approval";
  const canCancel =
    mode === "customer" &&
    !["completed", "cancelled", "refunded", "declined"].includes(b.status);

  const allowedStatuses = getAllowedStatusesByRole(
    (b.status || "pending") as BookingStatus,
    userRole,
  );

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size={{ base: "full", md: "md" }}
    >
      <DrawerOverlay bg={theme.overlay} />
      <DrawerContent bg={theme.surfaceAlt}>
        <DrawerCloseButton />
        <DrawerHeader
          borderBottomWidth="1px"
          borderColor={theme.border}
          bg={theme.surface}
        >
          <HStack justify="space-between" pr={10} align="center">
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" color={theme.text}>
                Booking #{String(b._id).slice(-6)}
              </Text>
              <Text fontSize="xs" color={theme.textSubtle}>
                {new Date(b.pickupAt).toLocaleString()} →{" "}
                {new Date(b.returnAt).toLocaleString()}
              </Text>
            </VStack>

            <Badge
              colorScheme={statusColor(b.status)}
              px={2.5}
              py={1}
              borderRadius={cardsBorderRadii}
              textTransform="none"
            >
              {prettyStatus(b.status)}
            </Badge>
          </HStack>
        </DrawerHeader>

        <DrawerBody py={4}>
          <VStack spacing={4} align="stretch">
            <Box
              bg={theme.surface}
              p={3}
              borderRadius={cardsBorderRadii}
              border="1px solid"
              borderColor={theme.border}
              boxShadow={theme.shadowSm}
            >
              <BookingStatusTimeline status={b.status} />
            </Box>

            <Box
              bg={theme.surface}
              p={3}
              borderRadius={cardsBorderRadii}
              border="1px solid"
              borderColor={theme.border}
              boxShadow={theme.shadowSm}
            >
              <Text fontWeight="semibold" mb={3} color={theme.text}>
                Quick Actions
              </Text>

              <Flex gap={3} wrap="wrap" justify="flex-end">
                {onUpdateStatus && (
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      variant="outline"
                      isDisabled={allowedStatuses.length === 0}
                      borderColor={theme.borderStrong}
                      color={theme.text}
                      _hover={{ bg: theme.hover }}
                    >
                      Update Status
                    </MenuButton>
                    <MenuList
                      bg={theme.surface}
                      borderColor={theme.border}
                      boxShadow={theme.shadowMd}
                    >
                      {allowedStatuses.length === 0 ? (
                        <MenuItem isDisabled>No allowed updates</MenuItem>
                      ) : (
                        allowedStatuses.map((s) => (
                          <MenuItem
                            key={s}
                            onClick={() => onUpdateStatus(s)}
                            _hover={{ bg: theme.hover }}
                          >
                            {prettyStatus(s)}
                          </MenuItem>
                        ))
                      )}
                    </MenuList>
                  </Menu>
                )}

                {canCancel && (
                  <Button colorScheme="orange" onClick={onCancel}>
                    Cancel Booking
                  </Button>
                )}
                {canOwnerDecide && (
                  <>
                    <Button colorScheme="green" onClick={onApprove}>
                      Approve Booking
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={onDecline}
                    >
                      Decline Booking
                    </Button>
                  </>
                )}
              </Flex>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <InfoCard title="Dates">
                <VStack align="stretch" spacing={2}>
                  <KeyVal
                    label="Pickup"
                    value={new Date(b.pickupAt).toLocaleString()}
                  />
                  <KeyVal
                    label="Return"
                    value={new Date(b.returnAt).toLocaleString()}
                  />
                </VStack>
              </InfoCard>

              <InfoCard title="Contact">
                <VStack align="stretch" spacing={2}>
                  <KeyVal label="Phone" value={b.phoneNumber || "—"} />
                  <KeyVal label="User" value={b.userId || "—"} />
                </VStack>
              </InfoCard>

              <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
                <InfoCard title="Addresses">
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontSize="xs" color={theme.textSubtle} mb={1}>
                        Pickup Address
                      </Text>
                      <Text fontSize="sm" color={theme.textMuted}>
                        {b.pickupAddress?.text || "—"}
                      </Text>
                    </Box>

                    {b.deliveryRequested && (
                      <Box>
                        <Text fontSize="xs" color={theme.textSubtle} mb={1}>
                          Delivery Address
                        </Text>
                        <Text fontSize="sm" color={theme.textMuted}>
                          {b.deliveryAddress?.text || "—"}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </InfoCard>
              </Box>

              <InfoCard title="Compliance">
                <VStack align="stretch" spacing={2}>
                  <KeyVal label="Age" value={b.age ?? "—"} />
                  <KeyVal
                    label="Terms"
                    value={b.acceptedTerms ? "Accepted" : "Not accepted"}
                  />
                  <KeyVal
                    label="Insurance Plan"
                    value={b.insurancePlan || "none"}
                  />
                  <KeyVal
                    label="Delivery Requested"
                    value={b.deliveryRequested ? "Yes" : "No"}
                  />
                </VStack>
              </InfoCard>

              <InfoCard title="License">
                <VStack align="stretch" spacing={2}>
                  <KeyVal label="Number" value={license.licenseNumber || "—"} />
                  <KeyVal
                    label="Verification"
                    value={license.verificationStatus || "unverified"}
                  />
                  {license.licenseImageUrl && (
                    <Box>
                      <Text fontSize="xs" color={theme.textSubtle} mb={1}>
                        Uploaded Image URL
                      </Text>
                      <Text
                        fontSize="xs"
                        color={theme.primary}
                        wordBreak="break-all"
                      >
                        {license.licenseImageUrl}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </InfoCard>
            </SimpleGrid>

            <InfoCard title="Pricing Breakdown">
              <VStack align="stretch" spacing={2}>
                <KeyVal
                  label="Base subtotal"
                  value={money(price.baseSubtotal)}
                />

                {Number(price.longStayDiscount || 0) > 0 && (
                  <KeyVal
                    label="Long stay discount"
                    value={`- ${money(price.longStayDiscount)}`}
                  />
                )}

                {Number(price.promoDiscount || 0) > 0 && (
                  <KeyVal
                    label="Promo discount"
                    value={`- ${money(price.promoDiscount)}`}
                  />
                )}

                {Number(price.insuranceFee || 0) > 0 && (
                  <KeyVal
                    label="Insurance fee"
                    value={money(price.insuranceFee)}
                  />
                )}

                {Number(price.deliveryFee || 0) > 0 && (
                  <KeyVal
                    label="Delivery fee"
                    value={money(price.deliveryFee)}
                  />
                )}

                <Divider borderColor={theme.divider} />

                <KeyVal
                  label="Total due"
                  value={
                    <Text fontWeight="bold" color={theme.text}>
                      {money(price.totalDue)}
                    </Text>
                  }
                />
              </VStack>
            </InfoCard>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
